import { ChartType, MSLineProps, Series } from './MSLine.types'
import { buildChartTooltipHtml, ChartTooltipItem, chartTooltipBase } from '../chartTooltip'
import { CHART_COLORS, CHART_FONT_FAMILY, CHART_PALETTE, toChartNumber } from '../chartTheme'

const resolveSeriesColor = (series: Series, index: number): string => {
  return series.color || series.anchorBgColor || CHART_PALETTE[index % CHART_PALETTE.length]
}

const resolveOpacity = (alpha?: string): number => {
  if (alpha == null || alpha === '') return 1
  const parsed = Number(alpha)
  if (!Number.isFinite(parsed)) return 1
  return parsed > 1 ? parsed / 100 : parsed
}

export const buildMSLineOption = (
  props: Pick<MSLineProps, 'yAxisName' | 'numberPrefix' | 'dataset' | 'categories'>,
  chartType: ChartType
) => {
  const {
    yAxisName = 'Hours',
    numberPrefix,
    dataset = [],
    categories = []
  } = props

  const categoryLabels = (categories[0] && categories[0].category || [])
    .map((entry) => entry.label)
    .filter((label) => label != null)

  const series = dataset.map((item, index) => {
    const color = resolveSeriesColor(item, index)
    const opacity = resolveOpacity(item.alpha)
    const isBar = chartType === ChartType.BAR

    return {
      name: item.seriesname || (item.isBest ? 'Maximum value' : `Series ${index + 1}`),
      type: isBar ? 'bar' : 'line',
      data: (item.data || []).map((entry) => toChartNumber(entry.value)),
      itemStyle: { color, opacity: isBar ? opacity : 1 },
      lineStyle: { color, width: 2, opacity },
      symbol: 'circle',
      symbolSize: 8,
      showSymbol: true,
      emphasis: { focus: 'series' },
      z: item.isBest ? 1 : 2
    }
  })

  return {
    backgroundColor: 'transparent',
    color: CHART_PALETTE,
    grid: {
      left: 16,
      right: 16,
      top: 28,
      bottom: 8,
      containLabel: true
    },
    tooltip: {
      ...chartTooltipBase,
      trigger: 'axis',
      axisPointer: {
        type: chartType === ChartType.BAR ? 'shadow' : 'line',
        lineStyle: { color: CHART_COLORS.primaryContent }
      },
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const categoryLabel = items[0] && items[0].axisValueLabel
        const list: ChartTooltipItem[] = []
        items.forEach((item) => {
          const seriesMeta = dataset[item.seriesIndex] as Series
          if (!seriesMeta) return
          const dataPoint = seriesMeta.data && seriesMeta.data[item.dataIndex]
          if (seriesMeta.isBest) {
            if (dataPoint && dataPoint.value == null) return
            list.push({
              label: (dataPoint && dataPoint.entityName) || 'Maximum value',
              value: `${numberPrefix || ''}${item.value == null ? '' : item.value}`,
              image: dataPoint && dataPoint.entityImage
            })
            return
          }
          list.push({
            label: seriesMeta.seriesname || (dataPoint && dataPoint.entityName) || item.seriesName,
            value: `${numberPrefix || ''}${item.value == null ? '' : item.value}`,
            color: resolveSeriesColor(seriesMeta, item.seriesIndex),
            image: seriesMeta.image || (dataPoint && dataPoint.entityImage)
          })
        })

        return buildChartTooltipHtml(
          { label: yAxisName, value: categoryLabel },
          list
        )
      }
    },
    xAxis: {
      type: 'category',
      name: 'Period',
      nameLocation: 'middle',
      nameGap: 22,
      nameTextStyle: {
        color: CHART_COLORS.placeholderContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12,
        fontWeight: 400
      },
      data: categoryLabels,
      axisLine: { lineStyle: { color: CHART_COLORS.gridLine } },
      axisTick: { show: false },
      axisLabel: {
        color: CHART_COLORS.primaryContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12,
        hideOverlap: true
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisName,
      nameLocation: 'end',
      nameGap: 6,
      nameTextStyle: {
        color: CHART_COLORS.placeholderContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 11,
        fontWeight: 400,
        align: 'left',
        padding: [0, 0, 0, 0]
      },
      splitNumber: 6,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: CHART_COLORS.gridLine } },
      axisLabel: {
        color: CHART_COLORS.primaryContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12,
        formatter: (value: number) => `${numberPrefix || ''}${value}`
      }
    },
    series
  }
}
